import { z } from "zod";

import { BUILTIN_THEMES } from "./builtin/index.ts";
import {
  BUILTIN_THEME_NAMES,
  type BuiltinThemeName,
  Theme,
  type ThemeId,
} from "./theme.ts";
import { ThemedElementFields } from "./themeRef.ts";

/**
 * The org themes a caller has in hand, keyed by id.
 *
 * Data rather than a function: every paint path resolves synchronously, so the
 * themes have to be loaded before anything resolves, and a record is what the
 * editor already holds. Passed rather than global — the API is one process
 * serving every org.
 */
export type ThemeLookup = Record<ThemeId, Theme>;

const BUILTIN_ID_PREFIX = "builtin:";

const BUILTIN_NAMES = new Set<string>(BUILTIN_THEME_NAMES);

/** `builtin:blue` → `blue`, and nothing else → undefined. */
export const builtinThemeName = (id: ThemeId): BuiltinThemeName | undefined => {
  if (!id.startsWith(BUILTIN_ID_PREFIX)) return undefined;
  const name = id.slice(BUILTIN_ID_PREFIX.length);
  return BUILTIN_NAMES.has(name) ? (name as BuiltinThemeName) : undefined;
};

/** The template an id names, for the ten that ship in code. */
export const builtinTheme = (id: ThemeId): Theme | undefined => {
  const name = builtinThemeName(id);
  return name === undefined ? undefined : BUILTIN_THEMES[name];
};

/** A list of themes, keyed for resolution. */
export const themeLookupFor = (themes: Iterable<Theme>): ThemeLookup =>
  Object.fromEntries(Array.from(themes, (theme) => [theme.id, theme]));

/**
 * The file's theme fields, plus the themes needed to resolve them.
 *
 * The library rides on `fileTheme`, which already reaches every place a theme
 * is resolved, rather than a second parameter through twenty-odd signatures.
 * Kept apart from {@link ThemedElementFields}, whose fields are STORED on the
 * root element — a cache of rows must never reach file state.
 */
export const FileThemeContext = ThemedElementFields.extend({
  themes: z.array(Theme).optional(),
});
export type FileThemeContext = z.infer<typeof FileThemeContext>;

/** The lookup a file's context implies. */
export const themeLookupOf = (
  file: { themes?: Theme[] } | undefined,
): ThemeLookup => themeLookupFor(file?.themes ?? []);
